'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import classNames from '@/utils/classNames';

import type { ModalProps } from './Modal.types';

const baseClassName = ['group', 'relative', 'isolate', 'inset-0', 'z-100'].join(
  ' ',
);

/**
 * Every open modal registers here so only the topmost one reacts. The handlers
 * below live on `document`, so without this one Escape fired every mounted
 * modal's `onClose` and a preview opened inside a drawer closed the drawer too.
 *
 * Closed-but-mounted modals stay out, or one would sit on top of the stack and
 * swallow Escape for the modal the user can actually see.
 */
type OpenModal = { token: symbol; depth: number };

const openModals: OpenModal[] = [];

/**
 * Topmost is the deepest modal, not the last one registered. Effects run child
 * first, so two modals mounting in the same commit register the inner one
 * first and push order would hand the parent the event.
 */
const isTopmostModal = (token: symbol): boolean => {
  let top: OpenModal | null = null;
  for (const candidate of openModals) {
    if (!top || candidate.depth >= top.depth) {
      top = candidate;
    }
  }
  return top?.token === token;
};

/** How many modals deep this one is. Context crosses the portal, so a modal
 * rendered inside another modal's children sees the outer one's depth. */
const ModalDepthContext = createContext(0);

const Modal = <T extends React.ElementType>({
  children,
  appear: transition = true,
  className,
  onClose,
  open,
  nonBlocking = false,
  ...props
}: ModalProps<T>) => {
  const [stackToken] = useState(() => Symbol('modal'));
  const depth = useContext(ModalDepthContext) + 1;

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const entry: OpenModal = { token: stackToken, depth };
    openModals.push(entry);

    return () => {
      const index = openModals.indexOf(entry);
      if (index !== -1) {
        openModals.splice(index, 1);
      }
    };
  }, [open, depth, stackToken]);

  // TODO: This is temporary solution for modals without backdrop
  //  It should be removed when we headlessui supports outside click management (should be soon)
  useEffect(() => {
    // Gated on `open` as well: a closed but mounted modal can never be topmost,
    // so its two document listeners would only ever early-return.
    if (!open || typeof onClose !== 'function') {
      return undefined;
    }

    const abortController = new AbortController();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isTopmostModal(stackToken)) {
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement) {
          const { tagName } = activeElement;
          if (
            tagName === 'INPUT' ||
            tagName === 'TEXTAREA' ||
            tagName === 'SELECT' ||
            activeElement.isContentEditable
          ) {
            return;
          }
        }
        onClose?.(false);
      }
    };

    const handleClick = (event: MouseEvent) => {
      if (!isTopmostModal(stackToken)) {
        return;
      }
      const target = event.target as HTMLElement;
      if (target.closest('[data-backdrop]')) {
        onClose?.(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown, {
      signal: abortController.signal,
      capture: true,
    });

    document.addEventListener('click', handleClick, {
      signal: abortController.signal,
      capture: true,
    });

    return () => {
      abortController.abort();
    };
  }, [open, onClose, stackToken]);

  return (
    <Dialog
      className={classNames(baseClassName, className)}
      transition={transition}
      onClose={() => null}
      open={open}
      // Headless UI's switch for a dialog that doesn't take over the page: no
      // inert/aria-hidden on the rest of the document, no scroll lock, no focus
      // trap. A drawer is presented without a backdrop precisely so the page
      // behind it stays usable, and this is the only way to opt out — the mark
      // is otherwise re-applied every time a dropdown inside the drawer closes
      // and hands the top layer back. Stripping the mark by hand instead would
      // desync Headless UI's ref-count and silently disarm any modal opened on
      // top of the drawer, which still has to isolate the page.
      __demoMode={nonBlocking}
      {...props}
    >
      <ModalDepthContext.Provider value={depth}>
        {children}
      </ModalDepthContext.Provider>
    </Dialog>
  );
};

export default Modal;
